import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonLink } from '../button-link/ButtonLink';
import styles from './ReadMore.module.scss';

export interface ReadMoreProps {
  /** Character cutoff. When `children.length` exceeds this, the text is truncated and a "Read more" toggle is rendered. Defaults to `100`. */
  limit?: number;
  /** Plain-text content to display. Must be a string — not ReactNode — because truncation uses `slice(0, limit)`. */
  children: string;
}

/**
 * Inline truncation helper: shows the first `limit` characters of a
 * string followed by `…` and a "Read more" link that expands to the
 * full text (and back to "Show less").
 *
 * Pass plain text as `children`; for rich/markdown content use a
 * dedicated truncation component instead.
 */
export const ReadMore = ({ children, limit = 100 }: ReadMoreProps) => {
  const limitExceeded = limit < children.length;
  const [isReadMore, setIsReadMore] = useState(limitExceeded);
  const { t } = useTranslation('common');

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore);
  };

  const getText = () => {
    const text = children;

    if (isReadMore) {
      return `${text.slice(0, limit)}...`;
    }

    return text;
  };

  return (
    <span>
      {getText()}
      {limitExceeded && (
        <ButtonLink
          className={styles.readMoreButton}
          label={isReadMore ? t('common:readMore') : t('common:showLess')}
          onClick={toggleReadMore}
        />
      )}
    </span>
  );
};
